import React from 'react';
import { motion } from 'framer-motion';
import { FaRocket, FaUsers, FaCogs, FaLightbulb, FaProjectDiagram, FaBrain } from 'react-icons/fa';
import { Footer } from '../../components/shared/Footer';

const PlatformOverview = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6 }
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="max-w-4xl mx-auto px-6 py-16"
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
            Omnigeist Platform Overview
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            The world's first Transmedia IP Development Platform that transforms scattered creative ideas into structured, universe-scale intellectual properties through intelligent human-AI collaboration.
          </p>
        </motion.div>

        {/* What is Omnigeist */}
        <motion.section variants={itemVariants} className="mb-16">
          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-8">
            <h2 className="text-3xl font-bold mb-6 text-blue-400">What is Omnigeist?</h2>
            <div className="text-lg leading-relaxed space-y-4">
              <p className="text-gray-300">
                Omnigeist is a revolutionary platform that defines an entirely new software category: <strong>Transmedia IP Development Platforms</strong>. Unlike traditional writing tools that focus on single outputs, or AI tools that try to replace human creativity, Omnigeist creates a structured partnership between human imagination and AI capability.
              </p>
              <p className="text-gray-300">
                The platform transforms the creative process from "blank page paralysis" to guided universe building, taking creators from scattered ideas to coherent intellectual properties that can scale across infinite media formats while maintaining perfect consistency.
              </p>
            </div>
          </div>
        </motion.section>

        {/* Core Differentiators */}
        <motion.section variants={itemVariants} className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-green-400">How Omnigeist Differs from Traditional Tools</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
              <h3 className="text-lg font-bold mb-3 text-red-300">Traditional Writing Software</h3>
              <ul className="space-y-2 text-sm text-gray-300">
                <li>• Single format output</li>
                <li>• Manual consistency checking</li>
                <li>• No structural guidance</li>
                <li>• Assumes storytelling knowledge</li>
                <li>• Isolated creative process</li>
              </ul>
            </div>
            
            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
              <h3 className="text-lg font-bold mb-3 text-yellow-300">AI Writing Tools</h3>
              <ul className="space-y-2 text-sm text-gray-300">
                <li>• "Prompt and pray" approach</li>
                <li>• No narrative structure</li>
                <li>• Inconsistent outputs</li>
                <li>• Replaces human creativity</li>
                <li>• No learning component</li>
              </ul>
            </div>
            
            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
              <h3 className="text-lg font-bold mb-3 text-green-300">Omnigeist Platform</h3>
              <ul className="space-y-2 text-sm text-gray-300">
                <li>• Human-AI creative partnership</li>
                <li>• Structured storytelling education</li>
                <li>• Multi-format consistency</li>
                <li>• Context-aware workflows</li>
                <li>• Universe-scale IP building</li>
              </ul>
            </div>
          </div>
        </motion.section>

        {/* Target Audiences */}
        <motion.section variants={itemVariants} className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-purple-400">Who Benefits from Omnigeist</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
              <div className="flex items-center mb-4">
                <FaUsers className="text-3xl text-blue-400 mr-4" />
                <h3 className="text-xl font-bold">Individual Creators</h3>
              </div>
              <p className="text-gray-300 mb-4">
                Independent writers, game designers, and content creators who want to build universe-scale intellectual properties but lack access to professional storytelling frameworks and consistency management tools.
              </p>
              <div className="text-sm text-blue-300">
                <strong>Key Benefit:</strong> Transform scattered ideas into structured, expandable universes
              </div>
            </div>

            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
              <div className="flex items-center mb-4">
                <FaProjectDiagram className="text-3xl text-green-400 mr-4" />
                <h3 className="text-xl font-bold">Existing IP Owners</h3>
              </div>
              <p className="text-gray-300 mb-4">
                Studios, publishers, and franchise owners who need to manage narrative consistency across multiple storylines, media formats, and collaborative teams while expanding their intellectual properties.
              </p>
              <div className="text-sm text-green-300">
                <strong>Key Benefit:</strong> Maintain perfect consistency while scaling content production
              </div>
            </div>

            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
              <div className="flex items-center mb-4">
                <FaCogs className="text-3xl text-purple-400 mr-4" />
                <h3 className="text-xl font-bold">Collaborative Teams</h3>
              </div>
              <p className="text-gray-300 mb-4">
                Multi-author writing teams, development studios, and creative collectives who need coordinated workflows, permission management, and unified vision across distributed creative processes.
              </p>
              <div className="text-sm text-purple-300">
                <strong>Key Benefit:</strong> Coordinated creativity with maintained creative control
              </div>
            </div>

            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
              <div className="flex items-center mb-4">
                <FaLightbulb className="text-3xl text-orange-400 mr-4" />
                <h3 className="text-xl font-bold">Community Builders</h3>
              </div>
              <p className="text-gray-300 mb-4">
                Creators who want to build open-source narrative universes, enable fan contributions, or create collaborative storytelling ecosystems with proper attribution and monetization frameworks.
              </p>
              <div className="text-sm text-orange-300">
                <strong>Key Benefit:</strong> Enable community-driven universe building with creative control
              </div>
            </div>
          </div>
        </motion.section>

        {/* Core Value Proposition */}
        <motion.section variants={itemVariants} className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-orange-400">Why Creators Need This Platform</h2>
          
          <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-xl p-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4 text-orange-300">The Creator Economy Problem</h3>
                <ul className="space-y-3 text-gray-300">
                  <li>• <strong>Blank Page Paralysis:</strong> Great ideas but no structure to develop them</li>
                  <li>• <strong>Consistency Chaos:</strong> Impossible to track details across complex universes</li>
                  <li>• <strong>Format Fragmentation:</strong> Each medium requires separate, incompatible tools</li>
                  <li>• <strong>Scale Barriers:</strong> Can't compete with studio-level IP development</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-bold mb-4 text-red-300">The Omnigeist Solution</h3>
                <ul className="space-y-3 text-gray-300">
                  <li>• <strong>Guided Structure:</strong> From brainstorming to story beats with AI tutoring</li>
                  <li>• <strong>Context Management:</strong> AI remembers every detail, maintains consistency</li>
                  <li>• <strong>Universal Source:</strong> One narrative DNA, infinite format expressions</li>
                  <li>• <strong>Democratized Power:</strong> Studio-level capabilities for individual creators</li>
                </ul>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Platform Architecture Overview */}
        <motion.section variants={itemVariants} className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-blue-400">High-Level Platform Architecture</h2>
          
          <div className="space-y-6">
            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
              <div className="flex items-center mb-4">
                <FaBrain className="text-3xl text-green-400 mr-4" />
                <h3 className="text-xl font-bold">Context Management System (CxMS)</h3>
              </div>
              <p className="text-gray-300 mb-4">
                The foundational layer that tracks every narrative element—characters, relationships, world rules, events—across your entire universe. Maintains consistency while alerting you to potential conflicts, always keeping creative decisions in your hands.
              </p>
            </div>

            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
              <div className="flex items-center mb-4">
                <FaProjectDiagram className="text-3xl text-blue-400 mr-4" />
                <h3 className="text-xl font-bold">Hierarchical Universe Structure</h3>
              </div>
              <p className="text-gray-300 mb-4">
                Organize your intellectual property through <strong>Metaverse → Bubbleverse → World → Location</strong> hierarchy, enabling alternate timelines, collaborative spaces, and infinite creative exploration while maintaining core narrative DNA.
              </p>
            </div>

            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
              <div className="flex items-center mb-4">
                <FaRocket className="text-3xl text-purple-400 mr-4" />
                <h3 className="text-xl font-bold">AI-Powered Creation Tools</h3>
              </div>
              <p className="text-gray-300 mb-4">
                Intelligent assistants guide you through proven storytelling frameworks—from character development to story structure—while teaching you the craft. AI amplifies your creativity rather than replacing it.
              </p>
            </div>
          </div>
        </motion.section>

        {/* Call to Action */}
        <motion.section variants={itemVariants} className="text-center">
          <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-xl p-8">
            <h2 className="text-2xl font-bold mb-4">Ready to Build Your Universe?</h2>
            <p className="text-gray-300 mb-6">
              Join the early access program for Story Forge and experience the future of intellectual property development. Transform your scattered creative ideas into structured, expandable universes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-600 transition-all"
              >
                Join Early Access
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-3 border border-gray-500 rounded-lg font-semibold hover:border-gray-400 transition-all"
              >
                Learn More Concepts
              </motion.button>
            </div>
          </div>
        </motion.section>
      </motion.div>
      <Footer />
    </div>
  );
};

export default PlatformOverview; 